import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  Action,
  CountBy,
  EntrySectionUpdateEvent,
  FiltersOperator,
  MetricEntryBehavior,
} from 'common/query-builder/query-entry-section/types';
import {MetricEntry, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {BaseEntryRow} from 'common/query-builder/query-entry-section/row-types/base';
import {RetentionQuerySettingsUpdate} from 'common/report/retention/query-builder/retention-query-settings';
import {
  RetentionAlignmentUpdate,
  RetentionBucketSizeUpdate,
  RetentionUnitUpdate,
} from 'common/report/retention/query-builder/retention-criteria';
import {MetricType} from 'common/query-builder/query-block/constants';

import {RetentionMetricEntryRowEl} from './template';

export class RetentionMetricEntryRow extends BaseEntryRow<MetricEntry> {
  template = RetentionMetricEntryRowEl;

  isPlaceholder() {
    const behaviors = this.entry.behavior.behaviors || [];
    return !this.hasValidEvents() || behaviors.some((behavior) => !this.hasValidFilters(behavior));
  }

  hasValidEvents() {
    const behaviors = this.entry.behavior.behaviors || [];
    return behaviors.length === 2 && behaviors.every((behavior) => !!behavior.name);
  }

  hasValidFilters(behavior: MetricEntryBehavior) {
    if (!behavior.filters) {
      return true;
    }
    return behavior.filters.every((filter) => new PropertyFilter(filter).isValid());
  }

  getCountingMethod(): string {
    const {retentionType, retentionUnbounded} = this.entry.behavior;
    const {cumulative} = this.entry.measurement;

    if (retentionType === `addiction`) {
      if (cumulative) {
        return `cumulative`;
      }
      return `non-cumulative`;
    }

    if (retentionUnbounded) {
      return `unbounded`;
    }
    return `bounded`;
  }

  handleEntryUpdate = (ev: EntrySectionUpdateEvent<QueryEventEntry>) => {
    const {entries} = ev.detail;
    const behaviors: MetricEntryBehavior[] = entries.map((entry) => ({
      id: entry.event.custom ? entry.event.id : null,
      type: entry.event.custom ? MetricType.CustomEvent : MetricType.Event,
      name: entry.event.value,
      filters: entry.filters,
      filtersDeterminer: entry.filtersOperator === FiltersOperator.Or ? `any` : `all`,
    }));
    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        behaviors,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleSettingsChanged = (update: RetentionQuerySettingsUpdate) => {
    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        retentionUnbounded: Boolean(update.detail.unbounded),
      },
      measurement: {
        ...this.entry.measurement,
        cumulative: Boolean(update.detail.cumulative),
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});

    // TODO: emit track event
  };

  handleRetentionUnitChanged = (update: RetentionUnitUpdate) => {
    const {retentionUnit} = update.detail;

    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        retentionUnit,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleBucketSizesApplied = (update: RetentionBucketSizeUpdate) => {
    const {retentionUnit, bucketSizes} = update.detail;

    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        retentionUnit,
        retentionCustomBucketSizes: bucketSizes,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleToggleRetentionAlignment = (update: RetentionAlignmentUpdate) => {
    const {retentionAlignmentType} = update.detail;

    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        retentionAlignmentType,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleRetentionCountBySelected = (ev: CustomEvent<{item: CountBy}>) => {
    const countBy = ev.detail.item;
    const measurement = this.entry.measurement;

    measurement.aggregationOperator = countBy.aggregationOperator;
    if (countBy?.property) {
      measurement.property = countBy.property;
    }

    const entry = {
      ...this.entry,
      measurement: {
        ...this.entry.measurement,
        aggregationOperator: countBy.aggregationOperator,
        ...(countBy?.property ? {property: countBy.property} : {}),
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };
}
